import { useState, useEffect } from 'react';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';

/**
 * Hook para precargar recursos y mostrar el progreso
 */
export default function useResourcePreloader(resources = []) {
  const [loaded, setLoaded] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!resources || resources.length === 0) {
      setLoaded(0);
      setIsComplete(true);
      return;
    }

    // Resetear estado
    setLoaded(0);
    setIsComplete(false);

    // Funciones helper para cargar cada tipo de recurso
    const loadGLB = (url) => {
      return new Promise((resolve, reject) => {
        const loader = new GLTFLoader();
        loader.load(url, resolve, undefined, reject);
      });
    };

    const loadImage = (url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    };

    const loadVideo = (url) => {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.preload = 'auto';
        video.muted = true;
        video.playsInline = true;

        const cleanup = () => {
          video.removeEventListener('canplay', onCanPlay);
          video.removeEventListener('error', onError);
          video.src = '';
        };

        const onCanPlay = () => {
          cleanup();
          resolve(video);
        };

        const onError = (err) => {
          cleanup();
          reject(err);
        };

        video.addEventListener('canplay', onCanPlay);
        video.addEventListener('error', onError);
        video.src = url;
        video.load();
      });
    };

    const loadAudio = async (url) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const arrayBuffer = await response.arrayBuffer();
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      
      await audioContext.decodeAudioData(arrayBuffer);
      audioContext.close();
    };

    const loadEXR = (url) => {
      return new Promise((resolve, reject) => {
        const loader = new EXRLoader();
        loader.load(
          url,
          (texture) => {
            texture.dispose();
            resolve();
          },
          undefined,
          reject
        );
      });
    };

    // Cargar un recurso según su tipo
    const loadResource = async (resource) => {
      try {
        switch (resource.type) {
          case 'glb':
            await loadGLB(resource.url);
            break;
          case 'image':
            await loadImage(resource.url);
            break;
          case 'video':
            await loadVideo(resource.url);
            break;
          case 'audio':
            await loadAudio(resource.url);
            break;
          case 'exr':
            await loadEXR(resource.url);
            break;
          default:
            console.warn(`Tipo de recurso desconocido: ${resource.type}`);
        }
      } catch (err) {
        console.warn(`Error cargando ${resource.url}:`, err);
      }
    };

    // Cargar todos los recursos en paralelo
    const promises = resources.map((resource) => loadResource(resource));
    const total = resources.length;

    // Contar cuántos se cargaron
    promises.forEach((promise) => {
      promise.finally(() => {
        setLoaded((prev) => {
          const newLoaded = prev + 1;
          if (newLoaded === total) {
            setIsComplete(true);
          }
          return newLoaded;
        });
      });
    });
  }, [resources]);

  const progress = resources.length > 0 ? Math.round((loaded / resources.length) * 100) : 100;

  return { progress, isComplete, error: null };
}
